import { FlexColumnSpaced, FlexRow, FlexRowSmallSpacedCentered } from '@fox/layout/src/flex';
import { Padded } from '@fox/layout/src/style';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React, { PropsWithChildren, Ref, createContext, useContext, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import { z } from 'zod';
import { useTypedLocalStorage } from '../../hooks/use-local-storage';
import { debounce } from '../../utils/debounce';
import { tryParseJSONObject } from '../../utils/string';
import { Button } from '../button/index';
import { DirtyState } from '../dirty-state/index';
import { MuiAlert } from '../mui-alert/index';
import { MuiAlertSnackbar, useSnackbarAlert } from '../mui-snackbar/index';
import { useForceRender } from '../popover/hooks';
import { Tooltip } from '../tooltip/index';

export type PersistenceContext<TData = unknown, TSave = void> = {
	dataKey: string;
	/** Function to obtain the current data stored in the persistent context */
	data: () => TData | undefined;

	/** Function to retrieve whether the data stored locally could possibly be not sent to the server */
	dirty: () => boolean;

	reset: () => TData | Promise<TData>;

	/** Discard any changed and treat the context as unmodified. Used when we are going to immediately navigate away and explicitly want to avoid saving */
	discard: () => void;

	/**
	 * Request the data in the persistence context be updated, optionally requesting it be saved
	 *
	 * NOTE: If called from an object returned by @see usePersistenceContext data is tracked as a component ref, not as component state, so
	 * calling this function won't trigger component re-render. If you need certain UI elements to update after this call (eg. text fields),
	 * use un-controlled components or have local state copies of the relevant sub-sections of this data and ensure they are applied to
	 * the @param data argument before calling this function
	 *
	 * Alternatively if you are calling this function from an object returned by @see usePersistenceObjectUpdate or @see usePersistenceArrayUpdate it will
	 * trigger a component re-render and the above remarks can be disregarded
	 */
	updateData: (data: TData, save?: boolean) => void;

	/** Request the data be saved to the server - this will be debounced and not sent if it matches the current payload */
	save: (data?: TData) => void;

	/** Immediately save to the server the data */
	saveImmediate: (data?: TData, forceUnchanged?: boolean) => Promise<TSave>;

	/** retrieve when we last saved */
	lastSave?: () => moment.Moment | undefined;

	/** For when we know best - assume the persisted data is the JSON.stringify'd value */
	applyPersisted: (json: string) => void;

	forceRender: () => void;

	/**
	 * @deprecated use alwaysRerender
	 * Force a rerender across the entire persistence context tree */
	setData: (data: TData) => void;

	loading: boolean;
	/** If saving to server in progress */
	saving: boolean;
	error?: unknown;

	/** When a validation function is supplied, upon updating the data we will check the validation and indicate any errors preventing saving  */
	validation?: Record<string, string | undefined>;

	performValidation: () => Promise<ValidationResultTuple>;
	alwaysRerender?: boolean;
	sessionRecovery?: SessionRecovery;
};

export type ValidationResultTuple = readonly [true] | [false, Record<string, string>];

type SessionRecovery = {
	key: string;
	promptRestore: () => void;
	clearRecovery: () => void;
	storeRecoveryState: () => void;
};
// TODO: Add support for nested properties without breaking existing validation
export function zodPersistenceValidate<Output>(zodSchema: z.ZodType<Output>): (data: unknown) => ValidationResultTuple {
	return data => {
		const result = zodSchema.safeParse(data);
		if (result.success) {
			return [true];
		} else {
			return [
				false,
				result.error.issues.reduce((prev, a) => {
					prev[a.path.join('.') || 'root'] = a.message;
					return prev;
				}, {})
			];
		}
	};
}

type PartialOrNull<T> = {
	[P in keyof T]?: T[P] | null;
};
type PersistenceProviderProps<TData, TPersistResult> = {
	/**
	 * Loads the data from the server for the particular item.
	 * The implementor should handle whether this is a create or fetch in this function (where you can return a default object, for example)
	 */
	load?: () => TData | Promise<TData | Error> | Error;

	/** Supply initial data to the persistence context - this will only be used if  is not defined
	 * @see {@link load} The data loader function
	 * @default {} // (empty object)
	 */
	initialData?: TData;

	/** Updates the data to the server with the given async function.
	 * This should **only** deal with persisting, validation should be handled outside of this function.
	 * Other concerns like closing a drawer or popover related to this persistance should be handled by {@link PersistenceProviderProps.onPersisted} */
	persist?: (data: TData, previousData?: TData) => Promise<TPersistResult | undefined>;

	/** Event handler that will be called every time we have saved.
	 * Given the persist is async, the dirty state could become dirty again so we pass this so the handler can ensure we're still clean. */
	onPersisted?: (result: TPersistResult | undefined, dirty: boolean) => void;

	/**
	 * Event handler that applies the new persist result to the current data item. This is called
	 * after @see onPersisted
	 *
	 * Use this if you need to set things like new record ids (from the persist result) to
	 * the current data item
	 *
	 * @param result The persist result
	 * @param currentData The current data item
	 * @returns
	 */
	applyPersistenceResult?: (result: TPersistResult | undefined, currentData: TData) => void;

	/**
	 * The message to display if the current Persistence data is dirty
	 *
	 * **note** this may not be displayed if they are closing the browser, a generic message is shown instead. */
	dirtyMessage?: string;

	/** Specify how often saves should be committed to the server, in milliseconds
	 * @default 1000
	 */
	debounceSaves?: number;

	/**
	 * Specify whether to send the data to the server automatically using debounced save
	 */
	disableAutoSave?: boolean | ((data: TData) => boolean);

	validate?: (data: TData) => Promise<ValidationResultTuple> | ValidationResultTuple;

	alwaysRerender?: boolean;

	sessionRecoveryKey?: SessionRecovery['key'];
};

type PersistenceHookPropsType<T, TPersistResult> = Omit<PersistenceProviderProps<T, TPersistResult>, 'dirtyMessage' | 'sessionRecoveryKey'> & {
	sessionRecovery?: Omit<SessionRecovery, 'storeRecoveryState'>;
};

const Context = createContext<PersistenceContext | undefined>(undefined);
Context.displayName = 'PersistenceContext';

export function usePersistence<T, TPersistResult = void>({
	load,
	persist,
	onPersisted,
	applyPersistenceResult,
	debounceSaves,
	initialData,
	disableAutoSave = false,
	validate,
	alwaysRerender = false,
	sessionRecovery
}: PersistenceHookPropsType<T, TPersistResult>): PersistenceContext<T> {
	const committedJsonRef = useRef<string>();
	const [dataKey, setDataKey] = useState(nanoid());

	const [error, setError] = useState<undefined | unknown>();
	const [validation, setValidation] = useState<Record<string, string> | undefined>();
	const initialCopy = initialData ? JSON.parse(JSON.stringify(initialData)) : undefined;
	// deep clone the incoming data to ensure we can safely mutate it
	const dataRef = useRef<T>(initialCopy);

	const forceRender = useForceRender();
	// the date that we have committed data from (reliably so - this is captured at the start of a save or load)
	const committedSinceRef = useRef<moment.Moment | undefined>();
	const dirtyRef = useRef(false);

	// we only flag dirty when we are dirty and need to render the DirtyState component - otherwise we just update refs
	const [_, setDirty] = useState(!initialData);
	const [loading, setLoading] = useState(!!load);
	const [saving, setSaving] = useState(false);
	const firstSubmitRef = useRef<boolean>(false);
	const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());

	const [localStorage, setLocalStorage] = useTypedLocalStorage<T | undefined>(sessionRecovery?.key, undefined);

	const reset = async () => {
		committedSinceRef.current = moment();

		if (!load || typeof load !== 'function') {
			dataRef.current = initialCopy ?? {};
		} else {
			try {
				setError(undefined);
				setLoading(true);
				const loadedData = await load();
				if (loadedData instanceof Error) {
					setError(loadedData);
				} else {
					dataRef.current = loadedData;
				}
			} catch (e) {
				setError(e);
			}
			setLoading(false);
		}

		dirtyRef.current = false;
		// this will trigger a re-render with the data
		committedJsonRef.current = JSON.stringify(dataRef.current);
		setDirty(false);

		const isStorageDirty = localStorage && JSON.stringify(localStorage) !== committedJsonRef.current;
		if (isStorageDirty) {
			sessionRecovery?.promptRestore?.();
		}

		setDataKey(nanoid());

		return dataRef.current;
	};

	// load the data initially
	useEffect(() => {
		reset();
	}, []);

	function storeRecoveryState() {
		setLocalStorage(dataRef.current);
	}

	// this function only refers inputs or refs so doesn't need to be included in any dependency array
	const updateImmediate = (data: T) => {
		sessionRecovery?.clearRecovery?.();
		dataRef.current = data;
		const dirty = JSON.stringify(data) !== committedJsonRef.current;
		dirtyRef.current = dirty;
		// any difference and we're dirty
		setDirty(dirty);
		if (dirty && sessionRecovery?.key) {
			storeRecoveryState();
		}

		if (firstSubmitRef.current) performValidation();
		if (alwaysRerender || sessionRecovery?.key) forceRender();
	};

	async function performValidation(): Promise<ValidationResultTuple> {
		if (validate) {
			firstSubmitRef.current = true;
			const [valid, errors] = await Promise.resolve(validate(dataRef.current));
			if (!valid) {
				setValidation(errors);
				return [false, errors];
			}

			setValidation(undefined);
		}
		return [true] as const;
	}

	async function persistImmediate(
		// these functions are prefixed with `local` because we want to make sure we use the passed in copy
		// and not accidentally the outer prop - due to closures and the useCallback and debounce
		localPersist: ((data: T, previousData?: T) => Promise<TPersistResult | undefined>) | undefined,
		localOnPersisted: ((result: TPersistResult | undefined, dirty: boolean) => void) | undefined,
		data?: T,
		forceUnchanged?: boolean
	) {
		try {
			setSaving(true);

			if (!localPersist) throw new Error('Persist prop is undefined - no ability to send data to the server');

			if (data) updateImmediate(data);

			firstSubmitRef.current = true;

			const [valid] = await performValidation();
			if (!valid) return;

			const saveStart = moment();

			if (!forceUnchanged && JSON.stringify(dataRef.current) === committedJsonRef.current) {
				console.log('Not sending to server as data is unchanged');
				return;
			}

			// at the start of the process, we capture the current data payload
			const persistedData = JSON.stringify(dataRef.current);
			// then we call the persistence function, which will be a mutation
			const result = await localPersist(dataRef.current, tryParseJSONObject<T>(committedJsonRef.current));
			// then we store the original stringified data into what was committed
			committedJsonRef.current = persistedData;

			// after an update, we MAY have got a fresh save/update, in which case we may be dirty again
			const dirty = applyPersisted(persistedData);

			committedSinceRef.current = saveStart;

			localOnPersisted?.(result, dirty);

			// Run the applyPersistenceResult handler with our persist result and current data ref
			// update the committedJsonRef afterwards
			applyPersistenceResult?.(result, dataRef.current);
			committedJsonRef.current = JSON.stringify(dataRef.current);
			setLocalStorage(dirty ? dataRef.current : undefined);
		} finally {
			setSaving(false);
		}
	}

	function saveImmediateInternal(
		localPersist: ((data: T, previousData?: T) => Promise<TPersistResult | undefined>) | undefined,
		localOnPersisted: ((result: TPersistResult | undefined, dirty: boolean) => void) | undefined,
		data?: T,
		forceUnchanged?: boolean
	) {
		const queuedSave = saveQueueRef.current
			.catch(() => undefined)
			.then(() => persistImmediate(localPersist, localOnPersisted, data, forceUnchanged));
		saveQueueRef.current = queuedSave;
		return queuedSave;
	}

	// this function only refers inputs or refs so doesn't need to be included in any dependency array
	// this function is async internally, but only after it begins
	const saveToServerDebounced = React.useCallback(debounce(saveImmediateInternal, debounceSaves ?? 1000), []);

	const save = React.useCallback(
		function save(data?: T) {
			// data being passed in here will update state also
			if (data) {
				updateImmediate(data);
				if (typeof disableAutoSave === 'function' ? disableAutoSave(data) : disableAutoSave === true) {
					return;
				}
			}
			saveToServerDebounced(persist, onPersisted, data);
		},
		[persist]
	);

	const updateData = React.useCallback(
		async (data: T, performSave?: boolean) => {
			if (performSave) {
				save(data);
			} else {
				updateImmediate(data);
			}
		},
		[save]
	);

	function applyPersisted(persistedJson: string) {
		const dirty = JSON.stringify(dataRef.current) !== persistedJson;
		dirtyRef.current = dirty;
		setDirty(dirty);
		return dirty;
	}

	const contextValue = {
		dataKey,
		forceRender: () => forceRender(),
		setData: (data: T) => {
			updateData(data);
			forceRender();
		},
		data: () => dataRef.current,
		dirty: () => dirtyRef.current,
		reset,
		discard: () => {
			dirtyRef.current = false;
		},
		updateData,
		error,
		loading,
		saving,
		sessionRecovery: sessionRecovery
			? {
					...sessionRecovery,
					storeRecoveryState
				}
			: undefined,
		save,
		saveImmediate: (data?: T, force?: boolean) => {
			saveToServerDebounced.cancel();
			return saveImmediateInternal(persist, onPersisted, data, force);
		},
		lastSave: () => committedSinceRef.current,
		applyPersisted,
		validation,
		performValidation,
		alwaysRerender
	};
	return contextValue;
}

/**
 *  Do NOT use the apiRef prop to access data from this provider, will not re-render
 *  the parent component after the data is loaded
 */
export function PersistenceProvider<T = unknown, TPersistResult = unknown>(
	props: PropsWithChildren<PersistenceProviderProps<T, TPersistResult> & { apiRef?: Ref<PersistenceContext<T>> }>
) {
	const [snackbar, setSnackbar] = useSnackbarAlert();
	const [dirtyChanged, setDirtyChanged] = useState(false);
	const promptRestore = () => {
		setSnackbar({ message: 'You have previously unsaved changes', severity: 'info' });
	};
	const clearRecovery = () => {
		setSnackbar({ open: false });
	};
	const sessionRecovery: Omit<SessionRecovery, 'storeRecoveryState'> | undefined = props.sessionRecoveryKey
		? {
				key: props.sessionRecoveryKey,
				promptRestore,
				clearRecovery
			}
		: undefined;

	const contextValue = usePersistence({
		...props,
		sessionRecovery
	});

	const [localStorage] = useTypedLocalStorage<T | undefined>(contextValue.sessionRecovery?.key, undefined);

	useImperativeHandle(props.apiRef, () => contextValue);
	return (
		<>
			<MuiAlertSnackbar
				{...snackbar}
				action={
					<FlexRowSmallSpacedCentered>
						<Tooltip title={'Restores unsaved changes locally. Save manually to keep them.'}>
							<Button
								size="small"
								variant="secondary"
								startIconName="Restore"
								onClick={() => {
									if (localStorage) {
										contextValue.updateData(localStorage);
										setDirtyChanged(a => !a);
										setSnackbar({ open: false });
									} else {
										console.warn(
											'The restore local changes snackbar has been shown with no localStorage available. This may indicate a bug'
										);
									}
								}}
							>
								Restore
							</Button>
						</Tooltip>
					</FlexRowSmallSpacedCentered>
				}
			/>
			<Persistence {...contextValue} dirtyMessage={props.dirtyMessage}>
				<React.Fragment key={`${dirtyChanged}`}>{props.children}</React.Fragment>
			</Persistence>
		</>
	);
}

export function Persistence<T = unknown, TPersistResult = unknown>({
	children,
	dirtyMessage,
	...props
}: PropsWithChildren<PersistenceContext<T> & { dirtyMessage?: string }>) {
	return (
		<Context.Provider value={props}>
			<DirtyState
				dirty={props.dirty}
				message={dirtyMessage ?? 'You have some unsaved changes, are you sure you want to leave this page?'}
				onConfirm={props.sessionRecovery?.storeRecoveryState}
			/>
			{props.error ? (
				<Padded>
					<FlexColumnSpaced>
						<MuiAlert severity="error" color="error">
							An error occurred loading the record{' '}
						</MuiAlert>
						<FlexRow>
							<Button variant="primary" onClick={props.reset}>
								Reload
							</Button>
						</FlexRow>
					</FlexColumnSpaced>
				</Padded>
			) : (
				children
			)}
		</Context.Provider>
	);
}

/**
 * Static persistance Provider for cases when we don't want to load data to compare with original state etc
 */
export function StaticPersistenceProvider<TData>({ children, data }: { children: React.ReactNode; data: TData | undefined }) {
	const contextValue = {
		dataKey: useRef(nanoid()).current,
		data: () => data,
		dirty: () => false,
		discard: () => {},
		reset: async () => data,
		updateData: () => {},
		save: () => {},
		saveImmediate: async () => {},
		lastSave: () => moment(),
		applyPersisted: () => {},
		forceRender: () => {},
		setData: () => {},
		loading: false,
		saving: false,
		performValidation: () => Promise.resolve([true] as const)
	};

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function NestedPersistenceProvider<TParent, TChild>({
	children,
	selectChild,
	updateChild
}: {
	children: React.ReactNode;
	selectChild: (parent: TParent) => TChild;
	updateChild: (parent: TParent, child: TChild) => TParent;
}) {
	const parent = usePersistenceContext<TParent>();

	const contextValue: PersistenceContext<TChild> = {
		...parent,
		data: () => {
			const currentData = parent.data();
			if (!currentData) return undefined;
			return selectChild(currentData);
		},
		updateData: (updatedData, save) => {
			const currentData = parent.data();
			if (!currentData) throw new Error(`Parent data is undefined`);
			const updatedParent = updateChild(currentData, updatedData);
			parent.updateData(updatedParent, save);
		},
		save: updatedData => {
			const currentData = parent.data();
			if (!currentData) throw new Error(`Parent data is undefined`);
			const updatedParent = updateChild(currentData, updatedData);
			parent.save(updatedParent);
		},
		saveImmediate: async updatedData => {
			if (updatedData) {
				const currentData = parent.data();
				if (!currentData) throw new Error(`Parent data is undefined`);
				const updatedParent = updateChild(currentData, updatedData);
				await parent.saveImmediate(updatedParent);
			} else {
				await parent.saveImmediate();
			}
		},
		setData: () => {
			throw new Error(`Nested context does not support the setData function.`);
		},
		reset: async () => {
			const resetData = await parent.reset();
			return selectChild(resetData);
		}
	};

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

/** Get the persistence context for the current data */
export function usePersistenceContext<T = unknown>(): PersistenceContext<T> {
	const context = useContext(Context);

	if (!context) throw new Error('Missing persistence context');

	return context as PersistenceContext<T>;
}

/** Like usePersistenceContext but returns null instead of throwing when no provider is present. */
export function usePersistenceContextOptional<T = unknown>(): PersistenceContext<T> | null {
	return (useContext(Context) as PersistenceContext<T> | undefined) ?? null;
}

/**
 * A function to use Persistant Provider as a child component to track changes in Object
 * accepts T - variables of initial state
 * @returns:
 * - currentData - updated data for current moment
 * - updateData - state update function to update Object
 */
export function usePersistenceObjectUpdate<T>() {
	const context = usePersistenceContext<T>();
	return usePersistencePeerObjectUpdate(context);
}

/**
 * A function to use Persistant Provider to track changes in Object where a persistence context is passed in
 * accepts T - variables of initial state
 * @returns:
 * - currentData - updated data for current moment
 * - updateData - state update function to update Object
 */
export function usePersistencePeerObjectUpdate<T>(context: PersistenceContext<T>) {
	const rerender = useForceRender();

	const { data, updateData, alwaysRerender } = context;

	const partialUpdate = (update: Partial<T>, save?: boolean) => {
		updateData({ ...data(), ...update } as T, save);
	};
	return {
		...context,

		/** Retrieves the data at the time of rendering */
		currentData: data?.(),

		/** Provides an updater function that will update the context but not force a re-render of the hosting component. Can be used for uncontrolled components. */
		partialUpdate,

		/** Provides an updater function that will update the context and force a re-render of the hosting component. Must be used for controlled components */
		updateData: (update: Partial<T>, save?: boolean) => {
			partialUpdate(update, save);
			rerender();
		}
	};
}

/**
 * A function to use Persistant Provider to track changes in Array
 * accepts T - variables of initial state
 * @returns:
 * - currentData - updated data for current moment
 * - addItem - state update function to update Array
 */
export function usePersistenceArrayUpdate<T>() {
	const rerender = useReducer(a => !a, false)[1];

	const context = usePersistenceContext<T[]>();
	const { data, updateData } = context;

	return {
		...context,
		currentData: data?.(),
		addItem: (update: T) => {
			updateData([...(data?.() ?? []), update]);
			rerender();
		},
		removeItem: (index: number) => {
			const newArray = [...(data?.() ?? [])];
			newArray.splice(index, 1);
			updateData(newArray);
			rerender();
		}
	};
}
